import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import "../../Styles/ann-tokens.css";
import "../../Styles/marketing-base.css";
import "../../Styles/ann-home.css";
import { testimonials } from "./testimonialData";

/* Candidate data for the interactive confidence report */
const CANDIDATES = [
  {
    who: "ROL-2049 · Round 3 complete",
    name: "Aarti Pillai",
    role: "Senior CS Manager · 6y · Hyderabad · Job Post sourced",
    sources: "Generated 14m ago · 8 sources cited",
    p: 81,
    cta: "Make an offer",
    verdict:
      "<b>Recommend hire.</b> The strongest read on the must-haves on this slate; one gentle area for a 20-min round 2 on cross-functional influence.",
    forLabel: "4 strong signals",
    for: [
      { c: "<b>Led de-escalation training</b> across 40 reps · ticket-recovery up 28% in two quarters.", s: "performance review 2024 · 2 references concur" },
      { c: "<b>Ran the Q4 surge solo</b> · SLAs held when volume tripled.", s: "current-role ops dashboard, Q4" },
      { c: "<b>Strongest comparable interview signal</b> on this slate (A−).", s: "structured round · same rubric as the slate" },
      { c: "<b>Quietly mentors 6 juniors</b> · two promoted last cycle.", s: "org chart + promotion records" },
    ],
    gapLabel: "1 gentle gap",
    gaps: [
      { c: "<b>Cross-functional influence reads thin.</b> All evidence is CS-internal.", s: "no product/ops collaboration in record" },
    ],
    meta: { mh: "6 / 6", iv: "A−", rf: "1", rk: "#1/12" },
  },
  {
    who: "ROL-2049 · Round 3 complete",
    name: "Rahul Verma",
    role: "CS Team Lead · 5y · Pune · QuickSearch sourced",
    sources: "Generated 14m ago · 6 sources cited",
    p: 73,
    cta: "Schedule round 2",
    verdict:
      "<b>Lean hire — with reservations.</b> Solid operator; two areas worth pressure-testing before an offer. Comparable, not the top of the slate.",
    forLabel: "3 strong signals",
    for: [
      { c: "<b>Steady under load</b> · held a 30-rep floor through two peak seasons.", s: "tenure record + manager note" },
      { c: "<b>Clean escalation discipline</b> · low re-open rate on hard tickets.", s: "ticketing export, trailing 6 months" },
      { c: "<b>Good interview signal</b> (B+) · structured, a touch rehearsed.", s: "structured round · same rubric" },
    ],
    gapLabel: "2 gaps",
    gaps: [
      { c: "<b>No evidence of mentoring or team-building.</b> Individual strength, not yet a multiplier.", s: "no reports, no promo trail" },
      { c: "<b>Surge experience is partial.</b> Supported Q4, did not own it.", s: "role scope, current employer" },
    ],
    meta: { mh: "5 / 6", iv: "B+", rf: "2", rk: "#3/12" },
  },
  {
    who: "ROL-2049 · Round 2 complete",
    name: "Divya Thomas",
    role: "Senior CS Associate · 2y · Bengaluru · Upload",
    sources: "Generated 14m ago · 5 sources cited",
    p: 64,
    cta: "Keep warm",
    verdict:
      "<b>Hold — not yet.</b> Real promise, but a step short on the must-haves for this level today. Worth revisiting for a lighter role, or in a year.",
    forLabel: "2 strong signals",
    for: [
      { c: "<b>Fast learner</b> · promoted from associate inside 14 months.", s: "promotion record" },
      { c: "<b>Strong written communication</b> · drafts the team's macros.", s: "macro library authorship" },
    ],
    gapLabel: "2 gaps",
    gaps: [
      { c: "<b>Hasn't led a team.</b> The role manages 40; her largest span is 4.", s: "role scope vs. RoleDNA" },
      { c: "<b>No peak-season ownership yet.</b> The must-have this role is built around.", s: "RoleDNA must-have, unmet" },
    ],
    meta: { mh: "4 / 6", iv: "B", rf: "3", rk: "#6/12" },
  },
];

export default function Home() {
  const rootRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const cleanups = [];
    const observers = [];
    const $ = (sel) => root.querySelector(sel);
    const $$ = (sel) => Array.prototype.slice.call(root.querySelectorAll(sel));

    /* ── number count-up ── */
    function countUp(el, dur = 900) {
      const raw = (el.dataset.count || el.textContent).trim();
      const m = raw.match(/^([^\d]*)([\d.,]+)([^\d]*)$/);
      if (!m) { el.textContent = raw; return; }
      const prefix = m[1];
      const suffix = m[3];
      const hadComma = m[2].includes(",");
      const target = parseFloat(m[2].replace(/,/g, ""));
      if (reduceMotion || isNaN(target)) { el.textContent = raw; return; }
      const fmt = (n) => {
        let s = (Number.isInteger(target) ? Math.round(n) : n.toFixed(1)).toString();
        if (hadComma) s = Math.round(n).toLocaleString("en-IN");
        return prefix + s + suffix;
      };
      const t0 = performance.now();
      (function tick(now) {
        const p = Math.min(1, (now - t0) / dur);
        const eased = 1 - Math.pow(1 - p, 3);
        el.textContent = fmt(target * eased);
        if (p < 1) requestAnimationFrame(tick);
        else el.textContent = raw;
      })(t0);
    }
    function countPanel(panel) {
      if (!panel) return;
      panel.querySelectorAll(".vol-counter .v, .lead-summary .v").forEach((el, i) => {
        if (!el.dataset.count) el.dataset.count = el.textContent.trim();
        setTimeout(() => countUp(el), i * 70);
      });
    }

    /* ── role switcher tabs ── */
    const tabBtns = $$(".tab-btn");
    const tabPanels = $$(".tab-panel");
    tabBtns.forEach((btn) => {
      const handler = () => {
        const tab = btn.dataset.tab;
        tabBtns.forEach((b) => b.setAttribute("aria-selected", b.dataset.tab === tab));
        tabPanels.forEach((p) => {
          const on = p.dataset.tab === tab;
          p.classList.toggle("active", on);
          if (on) countPanel(p);
        });
      };
      btn.addEventListener("click", handler);
      cleanups.push(() => btn.removeEventListener("click", handler));
    });

    /* ── résumé-read reveal ── */
    const rd = $("#resumeDemo");
    const rdBtn = $("#rdBtn");
    if (rd && rdBtn) {
      const handler = () => {
        const on = rd.classList.toggle("read");
        rdBtn.textContent = on ? "Reset" : "Read it like Ann";
      };
      rdBtn.addEventListener("click", handler);
      cleanups.push(() => rdBtn.removeEventListener("click", handler));
    }

    /* ── confidence report candidate switcher ── */
    const crCard = $("#crCard");
    let current = 0;
    function countNum(el, target, dur = 900) {
      if (reduceMotion) { el.textContent = target; return; }
      const from = parseFloat(el.textContent) || 0;
      const t0 = performance.now();
      (function tick(now) {
        const p = Math.min(1, (now - t0) / dur);
        const eased = 1 - Math.pow(1 - p, 3);
        el.textContent = Math.round(from + (target - from) * eased);
        if (p < 1) requestAnimationFrame(tick);
        else el.textContent = target;
      })(t0);
    }
    function setDial(p) {
      if (!crCard) return;
      crCard.querySelector(".dial").style.setProperty("--p", p);
      countNum(crCard.querySelector(".dial .num"), p, 1000);
    }
    function evRows(list, gap) {
      return list
        .map(
          (r) =>
            `<li${gap ? ' class="gap"' : ""}><span class="d"></span>` +
            `<span class="ev-body"><span class="ev-claim">${r.c}</span>` +
            `<span class="src">${r.s}</span></span></li>`
        )
        .join("");
    }
    function renderCandidate(idx) {
      if (!crCard) return;
      current = idx;
      const c = CANDIDATES[idx];
      crCard.querySelector("#crWho").textContent = c.who;
      crCard.querySelector("#crName").textContent = c.name;
      crCard.querySelector("#crRole").textContent = c.role;
      crCard.querySelector("#crVerdict").innerHTML = c.verdict;
      crCard.querySelector("#crSources").textContent = c.sources;
      crCard.querySelector("#crCta").textContent = c.cta;
      crCard.querySelector("#crEvidence").innerHTML =
        `<div class="ev-section"><div class="lbl"><span>What Ann saw</span>` +
        `<span style="color: oklch(0.40 0.10 155);">${c.forLabel}</span></div>` +
        `<ul>${evRows(c.for, false)}</ul></div>` +
        `<div class="ev-section"><div class="lbl"><span>Where to probe</span>` +
        `<span style="color: oklch(0.55 0.13 65);">${c.gapLabel}</span></div>` +
        `<ul>${evRows(c.gaps, true)}</ul></div>`;
      crCard.querySelector("#crMeta").innerHTML =
        `<div class="cell"><div class="k">Must-haves</div><div class="v">${c.meta.mh}</div></div>` +
        `<div class="cell"><div class="k">Interview</div><div class="v">${c.meta.iv}</div></div>` +
        `<div class="cell"><div class="k">Risk flags</div><div class="v">${c.meta.rf}</div></div>` +
        `<div class="cell"><div class="k">Slate rank</div><div class="v">${c.meta.rk}</div></div>`;
      crCard
        .querySelectorAll(".cr-chip")
        .forEach((ch) => ch.setAttribute("aria-selected", +ch.dataset.cand === idx));
    }
    if (crCard) {
      renderCandidate(0);
      crCard.querySelectorAll(".cr-chip").forEach((chip) => {
        const handler = () => {
          const idx = +chip.dataset.cand;
          if (idx === current) return;
          crCard.classList.add("swapping");
          setTimeout(() => {
            renderCandidate(idx);
            crCard.classList.remove("swapping");
            if (crCard.classList.contains("built")) setDial(CANDIDATES[idx].p);
          }, 220);
        };
        chip.addEventListener("click", handler);
        cleanups.push(() => chip.removeEventListener("click", handler));
      });
      const crEv = crCard.querySelector("#crEvidence");
      if (crEv) {
        const handler = (e) => {
          const li = e.target.closest("li");
          if (li) li.classList.toggle("src-open");
        };
        crEv.addEventListener("click", handler);
        cleanups.push(() => crEv.removeEventListener("click", handler));
      }
    }

    /* ── intersection-driven reveals ── */
    if ("IntersectionObserver" in window) {
      const eyeOb = new IntersectionObserver(
        (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("lit"); }),
        { rootMargin: "0px 0px -20% 0px" }
      );
      $$(".eye").forEach((el) => eyeOb.observe(el));
      observers.push(eyeOb);

      const actOb = new IntersectionObserver(
        (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("in"); }),
        { rootMargin: "0px 0px -45% 0px" }
      );
      $$(".e-act").forEach((el) => actOb.observe(el));
      observers.push(actOb);

      const dialOb = new IntersectionObserver(
        (entries, ob) => {
          entries.forEach((e) => {
            if (!e.isIntersecting) return;
            const card = e.target;
            card.classList.add("built");
            const num = card.querySelector(".dial .num");
            if (num) num.textContent = "0";
            setTimeout(() => setDial(CANDIDATES[current].p), 120);
            ob.unobserve(card);
          });
        },
        { threshold: 0.4 }
      );
      $$(".cr-card").forEach((el) => dialOb.observe(el));
      observers.push(dialOb);
    } else {
      $$(".eye").forEach((el) => el.classList.add("lit"));
      $$(".e-act").forEach((el) => el.classList.add("in"));
      $$(".cr-card").forEach((el) => el.classList.add("built"));
    }

    /* ── spine fill tracks scroll ── */
    const actRow = $(".e-act-row");
    const spineFill = $(".spine-fill");
    if (actRow && spineFill && !reduceMotion) {
      let ticking = false;
      const updateSpine = () => {
        const r = actRow.getBoundingClientRect();
        const clamped = Math.max(0, Math.min(1, (window.innerHeight * 0.75 - r.top) / r.height));
        spineFill.style.setProperty("--spine", (clamped * 100).toFixed(1) + "%");
        ticking = false;
      };
      const onScroll = () => { if (!ticking) { requestAnimationFrame(updateSpine); ticking = true; } };
      window.addEventListener("scroll", onScroll, { passive: true });
      cleanups.push(() => window.removeEventListener("scroll", onScroll));
      updateSpine();
    } else if (spineFill) {
      spineFill.style.setProperty("--spine", "100%");
    }

    /* default panel count after load */
    const t1 = setTimeout(() => countPanel($(".tab-panel.active")), 350);
    cleanups.push(() => clearTimeout(t1));

    /* ── section entrance choreography ── */
    const animEls = $$(".outcome, .aha, .switcher, .understands, .e-acts, .e-artifact, .e-modes, .e-bf, .e-proof, .e-faq, .e-cta");
    if (!reduceMotion && animEls.length) {
      document.documentElement.classList.add("js-anim");
      animEls.forEach((el) => el.classList.add("anim"));
      const reveal = (el) => el.classList.add("seen");
      const check = () => {
        const vh = window.innerHeight;
        for (let i = animEls.length - 1; i >= 0; i--) {
          const el = animEls[i];
          if (el.classList.contains("seen")) continue;
          const r = el.getBoundingClientRect();
          if (r.top < vh * 0.88 && r.bottom > 0) reveal(el);
        }
      };
      check();
      let ticking = false;
      const onScroll = () => { if (!ticking) { requestAnimationFrame(() => { check(); ticking = false; }); ticking = true; } };
      window.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("resize", onScroll, { passive: true });
      const t2 = setTimeout(() => animEls.forEach(reveal), 5000);
      cleanups.push(() => {
        window.removeEventListener("scroll", onScroll);
        window.removeEventListener("resize", onScroll);
        clearTimeout(t2);
      });
    }

    /* ── scroll progress hairline ── */
    const prog = $("#scrollProgress");
    if (prog && !reduceMotion) {
      let ticking = false;
      const upd = () => {
        const h = document.documentElement.scrollHeight - window.innerHeight;
        prog.style.width = (h > 0 ? (window.scrollY / h) * 100 : 0) + "%";
        ticking = false;
      };
      const onScroll = () => { if (!ticking) { requestAnimationFrame(upd); ticking = true; } };
      window.addEventListener("scroll", onScroll, { passive: true });
      cleanups.push(() => window.removeEventListener("scroll", onScroll));
      upd();
    }

    return () => {
      cleanups.forEach((fn) => fn());
      observers.forEach((ob) => ob.disconnect());
      document.documentElement.classList.remove("js-anim");
    };
  }, []);

  return (
    <div className="page ann-marketing" ref={rootRef}>
      <div className="scroll-progress" id="scrollProgress" aria-hidden="true" />

      {/* HERO */}
      <section className="hero-e" id="top">
        <div className="frame" />

        <span className="eye-strong">Hiring intelligence</span>

        <p className="hook">
          Most hiring decisions are made on almost no real information — and
          everyone in the room quietly knows it.
        </p>

        <h1>
          Every recruiting tool automates hiring.
          <br />
          <span className="ann-says">
            Ann is the first that <em>understands</em>
          </span>{" "}
          it<span className="period" />
        </h1>

        <div className="sub-row">
          <p className="lede-l">
            Ann finds the best-fit candidates, reaches out, and evaluates them —{" "}
            <b>every step held to a state-of-the-art standard</b>. One
            intelligence that reads a role the way your sharpest recruiter would,
            and surfaces the people others miss.
          </p>
          <div className="ctas">
            <Link to="/contact-us" className="btn btn-primary btn-lg">
              Book a demo
            </Link>
            <a href="#switcher" className="btn btn-secondary btn-lg">
              See it work
            </a>
          </div>
        </div>
      </section>

      {/* OUTCOME LINE */}
      <div className="outcome">
        <div className="grid">
          <div className="lbl">The result</div>
          <p className="text">
            Faster, sharper, more confident hiring — because, for the first time,
            your software actually <em>gets it</em>
            <b>.</b>
          </p>
        </div>
      </div>

      {/* THE AHA */}
      <section className="aha">
        <div className="inner">
          <span className="eye">The cost nobody sees</span>
          <p className="aha-line">
            The best hire you didn't make wasn't rejected.
            <br />
            You <em>never saw</em> them.
          </p>
          <p className="aha-sub">
            <b>
              Keyword search rewards the people who wrote their résumé like the
              job post.
            </b>{" "}
            The ones who did the work — and described it plainly — were never
            rejected. They were simply invisible. Every search you've run had one.
          </p>
        </div>
      </section>

      {/* ROLE SWITCHER */}
      <section className="switcher" id="switcher">
        <div className="head">
          <div>
            <span className="eye">For any role</span>
            <div className="spacer-2" />
            <h2>
              200 customer service hires? <em>Or one CFO?</em>
            </h2>
          </div>
          <p className="desc">
            Ann isn't built for a tier of hiring. <b>She's built for hiring.</b>{" "}
            Pick a role — see the gear Ann runs in. Same intelligence throughout.
          </p>
        </div>

        <div className="tabs" role="tablist">
          <button className="tab-btn" role="tab" aria-selected="true" data-tab="volume">
            <span className="num">01 · Volume</span>
            <div className="ttl">
              When you need <em>200 of them</em>.
            </div>
            <div className="sub">Bulk hiring. CS, ops, sales floor, healthcare aides, BPO.</div>
          </button>
          <button className="tab-btn" role="tab" aria-selected="false" data-tab="spec">
            <span className="num">02 · Specialised</span>
            <div className="ttl">
              When you need <em>exactly the right 5</em>.
            </div>
            <div className="sub">Skilled roles. Engineers, designers, RNs, analysts.</div>
          </button>
          <button className="tab-btn" role="tab" aria-selected="false" data-tab="lead">
            <span className="num">03 · Headhunt</span>
            <div className="ttl">
              When you need <em>the one</em>.
            </div>
            <div className="sub">Leadership search. CFOs, VPs, GMs, founders' lieutenants.</div>
          </button>
        </div>

        <div className="tab-panels">
          {/* Volume panel */}
          <div className="tab-panel active" data-tab="volume" role="tabpanel">
            <div className="panel-says">
              <div className="sig">
                <span className="g" />
                <span className="for">Volume mode</span>
              </div>
              <p className="speech">
                Two hundred customer-service hires this month. <b>Ann</b> reads{" "}
                <span className="n">12,400</span> applicants the way a careful
                recruiter would, then surfaces the <span className="n">~400</span>{" "}
                worth a twenty-minute call.
                <br />
                <br />
                Same care at row <em>1</em>. Same care at row <em>12,400</em>.
              </p>
              <div className="speech-extras">
                <div className="item">
                  <strong>12,400</strong>
                  Résumés read · this role
                </div>
                <div className="item">
                  <strong>~400</strong>
                  Worth a 20-min call
                </div>
              </div>
            </div>

            <div className="panel-vis">
              <div className="topbar">
                <div className="dots"><span /><span /><span /></div>
                <span>Project · CS Associate · 200 openings · Hyderabad</span>
                <span className="live">Ann live</span>
              </div>
              <div className="body">
                <div className="vol-counter">
                  <div className="cell"><div className="v">12,4k</div><div className="k">Read</div></div>
                  <div className="cell"><div className="v">3,180</div><div className="k">Qualifies</div></div>
                  <div className="cell featured"><div className="v">412</div><div className="k">Worth calling</div></div>
                  <div className="cell"><div className="v">18d</div><div className="k">To full</div></div>
                </div>
                <div className="vol-rows">
                  <div className="r"><div className="av" style={{ background: "var(--src-jobpost)" }}>AP</div><div className="nm">Aarti Pillai <span className="role" style={{ color: "var(--ink-3)", fontWeight: 400 }}>· CS · 3y</span></div><span className="scr h">92</span></div>
                  <div className="r"><div className="av" style={{ background: "var(--src-quick)" }}>MR</div><div className="nm">Manish Rao <span className="role" style={{ color: "var(--ink-3)", fontWeight: 400 }}>· Inside sales · 4y</span></div><span className="scr h">88</span></div>
                  <div className="r"><div className="av" style={{ background: "var(--src-jobpost)" }}>SK</div><div className="nm">Sanjana Kumar <span className="role" style={{ color: "var(--ink-3)", fontWeight: 400 }}>· BPO · 5y</span></div><span className="scr h">87</span></div>
                  <div className="r"><div className="av" style={{ background: "var(--src-quick)" }}>DT</div><div className="nm">Divya Thomas <span className="role" style={{ color: "var(--ink-3)", fontWeight: 400 }}>· CS lead · 2y</span></div><span className="scr m">79</span></div>
                  <div className="r"><div className="av" style={{ background: "var(--src-upload)" }}>RV</div><div className="nm">Rahul Verma <span className="role" style={{ color: "var(--ink-3)", fontWeight: 400 }}>· Retail · 4y</span></div><span className="scr m">76</span></div>
                  <div className="more">+ 407 more, sorted by fit</div>
                </div>
              </div>
            </div>
          </div>

          {/* Specialised panel */}
          <div className="tab-panel" data-tab="spec" role="tabpanel">
            <div className="panel-says">
              <div className="sig">
                <span className="g" />
                <span className="for">Specialised mode</span>
              </div>
              <p className="speech">
                Five paediatric ICU nurses with NICU experience. Not a hundred —{" "}
                <em>the eight who actually have it</em>: <span className="n">3</span>{" "}
                from a public listing, <span className="n">5</span> sourced from
                their published work, peer references, and the hospitals where
                they've been quietly excellent.
              </p>
              <div className="speech-extras">
                <div className="item">
                  <strong>2,100</strong>
                  Profiles mapped
                </div>
                <div className="item">
                  <strong>8</strong>
                  Match your real bar
                </div>
              </div>
            </div>

            <div className="panel-vis">
              <div className="topbar">
                <div className="dots"><span /><span /><span /></div>
                <span>Project · Paediatric ICU RN · 5 openings · Pune</span>
                <span className="live">Ann live</span>
              </div>
              <div className="body">
                <div className="spec-list">
                  <div className="spec-card">
                    <div className="top">
                      <div className="av" style={{ background: "var(--src-talent)" }}>AM</div>
                      <span className="nm">Dr. Anjali Mehta</span>
                      <span className="scr">94</span>
                    </div>
                    <p className="reason"><em>NICU lead at AIIMS · authored 2 papers on neonatal sepsis.</em> Not actively looking — open to conversation.</p>
                  </div>
                  <div className="spec-card">
                    <div className="top">
                      <div className="av" style={{ background: "var(--src-quick)" }}>PJ</div>
                      <span className="nm">Priya Joshi, RN</span>
                      <span className="scr">91</span>
                    </div>
                    <p className="reason"><em>6 years PICU at Apollo</em> · referenced in 4 hospital case studies as the on-call you want.</p>
                  </div>
                  <div className="spec-card">
                    <div className="top">
                      <div className="av" style={{ background: "var(--src-jobpost)" }}>VK</div>
                      <span className="nm">Vidya Krishnan</span>
                      <span className="scr">88</span>
                    </div>
                    <p className="reason"><em>Applied last week.</em> Came up because of her trauma-unit cross-training — not visible on the résumé top line.</p>
                  </div>
                  <div className="spec-card">
                    <div className="top">
                      <div className="av" style={{ background: "var(--src-talent)" }}>NS</div>
                      <span className="nm">Neha Sharma, RN</span>
                      <span className="scr">87</span>
                    </div>
                    <p className="reason"><em>Published a protocol on ventilator weaning.</em> Manage 12-bed PICU shifts solo on nights.</p>
                  </div>
                  <div className="spec-card">
                    <div className="top">
                      <div className="av" style={{ background: "var(--src-upload)" }}>RD</div>
                      <span className="nm">Ritu D'Souza</span>
                      <span className="scr">85</span>
                    </div>
                    <p className="reason"><em>Referral from your medical director.</em> Two former colleagues on staff vouch quietly.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Headhunt panel */}
          <div className="tab-panel" data-tab="lead" role="tabpanel">
            <div className="panel-says">
              <div className="sig">
                <span className="g" />
                <span className="for">Headhunt mode</span>
              </div>
              <p className="speech">
                A CFO who's been through both an <em>IPO</em> and a{" "}
                <em>downturn</em>.
                <br />
                <br />
                Every CFO in your industry who fits — <span className="n">47</span>{" "}
                of them, mapped. The <span className="n">6</span> worth talking to,
                with the right opening line for each.
              </p>
              <div className="speech-extras">
                <div className="item">
                  <strong>47</strong>
                  Mapped to profile
                </div>
                <div className="item">
                  <strong>6</strong>
                  Recommended outreach
                </div>
              </div>
            </div>

            <div className="panel-vis">
              <div className="topbar">
                <div className="dots"><span /><span /><span /></div>
                <span>Project · CFO · 1 opening · India / Remote</span>
                <span className="live">Ann live</span>
              </div>
              <div className="body">
                <div className="lead-summary">
                  <div className="cell"><div className="v">47</div><div className="k">Mapped</div></div>
                  <div className="cell"><div className="v">12</div><div className="k">Pre-vetted</div></div>
                  <div className="cell"><div className="v">6</div><div className="k">Recommend</div></div>
                </div>
                <div className="lead-card">
                  <div className="row1">
                    <div className="av">VB</div>
                    <div className="nm">Vikram Bhat <span>· Group CFO · ed-tech unicorn · Bengaluru</span></div>
                  </div>
                  <p className="why">Led the <b>2019 IPO</b> and restructured spend through the <b>2022 downturn</b>. <em>Lead with the public-markets readiness story; he'll engage.</em></p>
                  <div className="tags">
                    <span className="tag">IPO experience</span>
                    <span className="tag">downturn led</span>
                    <span className="tag">peer ref · 3</span>
                  </div>
                </div>
                <div className="lead-card">
                  <div className="row1">
                    <div className="av">SL</div>
                    <div className="nm">Sarika Lall <span>· CFO · D2C consumer · Mumbai</span></div>
                  </div>
                  <p className="why">Quiet operator. <b>2 successful exits</b> in your category. <em>Open to a private conversation — not to a recruiter cold-email.</em></p>
                  <div className="tags">
                    <span className="tag">2 exits</span>
                    <span className="tag">category fit</span>
                    <span className="tag">warm intro · 1</span>
                  </div>
                </div>
                <div className="lead-card">
                  <div className="row1">
                    <div className="av">DK</div>
                    <div className="nm">Divya Krishnamurthy <span>· VP Finance → CFO-ready · Hyderabad</span></div>
                  </div>
                  <p className="why">Under-titled. Has run finance solo for 4 years at a series-D. <em>The step-up candidate your search would miss.</em></p>
                  <div className="tags">
                    <span className="tag">under-titled</span>
                    <span className="tag">step-up</span>
                    <span className="tag">step-up case · solid</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT ANN UNDERSTANDS */}
      <section className="understands">
        <div className="head">
          <span className="eye">What Ann understands</span>
          <h2>
            It sees them because it <em>understands</em> first.
          </h2>
          <p className="lede">
            The candidate you missed wasn't hidden by accident — a tool reading
            keywords filtered them out. <b>Ann reads context first.</b> That's the
            difference between automating hiring and understanding it.
          </p>
        </div>

        <div className="u-grid">
          <div className="u-card">
            <div className="num">01 / 03</div>
            <h3 className="h">
              Your <em>company</em>.
            </h3>
            <p className="says">
              How you build, who you've hired well, how you fight. A senior who
              thrives at your competitor might be <em>wrong</em> for you — Ann
              knows that <b>before</b> the search starts.
            </p>
            <div className="vis">
              <div className="vk">CompanyDNA · sample signal</div>
              <div className="vv">"Ships in weeks not quarters, prefers operators over strategists, values quiet competence over presentation."</div>
            </div>
          </div>
          <div className="u-card">
            <div className="num">02 / 03</div>
            <h3 className="h">
              The <em>role</em>.
            </h3>
            <p className="says">
              Not the JD — the <em>work</em>. A 'Senior CS Manager' here means
              leading 40 reps through a Q4 surge. Your hiring manager knows that;
              the JD doesn't. <b>Ann does.</b>
            </p>
            <div className="vis">
              <div className="vk">RoleDNA · what the work really is</div>
              <div className="vv">"Sweeps 40+ reps through Q4 surge · de-escalates the 2% of hard tickets · the manager's right hand in week 5."</div>
            </div>
          </div>
          <div className="u-card">
            <div className="num">03 / 03</div>
            <h3 className="h">
              The <em>candidate</em>.
            </h3>
            <p className="says">
              Every résumé end-to-end — the <em>work</em>, not the keywords.
              Cross-trained, under-titled, career switcher: read the way a careful
              human would, <b>every time</b>.
            </p>
            <div className="resume-demo" id="resumeDemo">
              <div className="rd-top">
                <span>Résumé · <span className="pg">p.1</span></span>
                <button className="rd-btn" id="rdBtn" type="button">Read it like Ann</button>
              </div>
              <div className="rd-body">
                <div className="rd-line">
                  <span className="rd-raw">"Handled escalations on the support floor."</span>
                  <div className="rd-note">
                    <span className="gly" />
                    <span className="txt">De-escalation lead across 40 reps — not just "handled".</span>
                  </div>
                </div>
                <div className="rd-line">
                  <span className="rd-raw">"Was around during our busiest quarter."</span>
                  <div className="rd-note">
                    <span className="gly" />
                    <span className="txt">Ran the Q4 surge solo. SLAs held at 3× volume.</span>
                  </div>
                </div>
                <div className="rd-line">
                  <span className="rd-raw">"Helped new joiners settle in."</span>
                  <div className="rd-note">
                    <span className="gly" />
                    <span className="txt">Mentored 6 juniors · 2 promoted. A multiplier.</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* THE ACTS */}
      <section className="e-acts" id="acts">
        <div className="head">
          <span className="eye">How Ann works</span>
          <div className="spacer-2" />
          <h2>
            Not a stack of features. <em>One intelligence.</em>
          </h2>
          <p className="lede">
            Six moves, every role — <em>one fabric</em>, not six tools stitched
            together. That's the only way the signal carries.
          </p>
        </div>

        <div className="e-act-row">
          <div className="spine-track" />
          <div className="spine-fill" />
          <div className="e-act">
            <span className="num">Act I</span>
            <h3 className="verb">Listen.</h3>
            <p className="says">Ann reads your company and the role first. By the time she opens a single profile, <em>she knows what to look for</em>.</p>
            <span className="arrow">→</span>
          </div>
          <div className="e-act">
            <span className="num">Act II</span>
            <h3 className="verb">Search.</h3>
            <p className="says">Two engines run together. <b>QuickSearch</b> finds the volume. <b>TalentGPS</b> headhunts the people not actively looking. Together — <em>everyone worth seeing</em>.</p>
            <span className="arrow">→</span>
          </div>
          <div className="e-act">
            <span className="num">Act III</span>
            <h3 className="verb">Read.</h3>
            <p className="says">Every résumé. Same care, the first one and the ten-thousandth. <em>Never skims. Never tires.</em></p>
            <span className="arrow">→</span>
          </div>
          <div className="e-act">
            <span className="num">Act IV</span>
            <h3 className="verb">Reach out.</h3>
            <p className="says">In your team's voice. You approve. Candidates get a message that sounds like the person who'll meet them — <em>because it is</em>.</p>
            <span className="arrow">→</span>
          </div>
          <div className="e-act">
            <span className="num">Act V</span>
            <h3 className="verb">Interview.</h3>
            <p className="says">Structured. Recorded. <em>Comparable</em>. Same rubric across every candidate, every round — so weeks later you can still tell who was really stronger.</p>
            <span className="arrow">→</span>
          </div>
          <div className="e-act">
            <span className="num">Act VI</span>
            <h3 className="verb">Recommend.</h3>
            <p className="says">A confidence report — the verdict, the case, the gaps. <b>The decision stays yours.</b> You just never make it blind.</p>
            <span className="arrow">→</span>
          </div>
        </div>

        <div className="pushback">
          <span className="g" />
          <p className="text">— And throughout, Ann <em>pushes back</em>: when the brief is thin, the bar is wrong, the verdict needs a second look. <b>A sounding board, not a stamp.</b></p>
        </div>

        <div className="result-strip">
          <div>
            <div className="lbl">The result</div>
            <div className="lead">Faster. Cheaper. <em>Sharper</em>.</div>
          </div>
          <div className="bullets">
            <div className="b">
              <div className="k">Faster</div>
              <div className="v">Brief to defensible shortlist in <em>days</em>, not weeks.</div>
            </div>
            <div className="b">
              <div className="k">Cheaper</div>
              <div className="v">One layer doing the work of a recruiter, a sourcer, and an interview coach.</div>
            </div>
            <div className="b">
              <div className="k">Sharper</div>
              <div className="v">More accurate hires — a <em>deeper read</em> on role and candidate than your team had time for.</div>
            </div>
            <div className="b">
              <div className="k">Auditable</div>
              <div className="v">Every verdict cited. Every step traceable. <em>Defensible</em> the day someone asks why.</div>
            </div>
          </div>
        </div>
      </section>

      {/* ARTIFACT */}
      <section className="e-artifact">
        <div className="head">
          <span className="eye">The artifact</span>
          <div className="spacer-2" />
          <h2>
            This is what arrives when Ann is <em>done</em>.
          </h2>
          <p className="lede">
            One document per candidate — verdict, evidence, gaps.{" "}
            <b>Forwardable to a hiring manager. Comparable across a slate.
            Defensible to a board.</b>
          </p>
        </div>

        <div className="cr-card" id="crCard">
          <div className="barbar">
            <span className="l">Hiring confidence report</span>
            <span className="r" id="crSources">Generated 14m ago · 8 sources cited</span>
          </div>
          <div className="cr-switch" role="tablist" aria-label="Compare candidates on this role">
            <span className="lbl">Same role · compare</span>
            <button className="cr-chip" role="tab" aria-selected="true" data-cand="0"><span className="rk">#1</span><span className="cn">Aarti Pillai</span></button>
            <button className="cr-chip" role="tab" aria-selected="false" data-cand="1"><span className="rk">#3</span><span className="cn">Rahul Verma</span></button>
            <button className="cr-chip" role="tab" aria-selected="false" data-cand="2"><span className="rk">#6</span><span className="cn">Divya Thomas</span></button>
          </div>
          <div className="body">
            <div className="left">
              <div className="who" id="crWho">ROL-2049 · Round 3 complete</div>
              <h3 id="crName">Aarti Pillai</h3>
              <p className="role" id="crRole">Senior CS Manager · 6y · Hyderabad · Job Post sourced</p>
              <div className="verdict">
                <div className="sig">
                  <span className="g" />
                  <span className="nm">Ann's verdict</span>
                </div>
                <p className="text" id="crVerdict"><b>Recommend hire.</b> The strongest read on the must-haves on this slate; one gentle area for a 20-min round 2 on cross-functional influence.</p>
              </div>
              <div id="crEvidence" />
            </div>
            <div className="side">
              <div className="dial" data-build>
                <div className="inner">
                  <span className="num" data-count="81">81</span>
                  <span className="lbl">Confidence</span>
                </div>
              </div>
              <div className="meta-grid" id="crMeta">
                <div className="cell"><div className="k">Must-haves</div><div className="v">6 / 6</div></div>
                <div className="cell"><div className="k">Interview</div><div className="v">A−</div></div>
                <div className="cell"><div className="k">Risk flags</div><div className="v">1</div></div>
                <div className="cell"><div className="k">Slate rank</div><div className="v">#1/12</div></div>
              </div>
              <div className="actions">
                <a href="#cta" className="btn btn-primary btn-sm" id="crCta">Make an offer</a>
                <a href="#cta" className="btn btn-secondary btn-sm">Schedule round 2</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MODES */}
      <section className="e-modes" id="modes">
        <div className="head">
          <span className="eye">Where to start</span>
          <div className="spacer-2" />
          <h2>
            Pick the part of the work to <em>hand over</em>.
          </h2>
        </div>

        <div className="grid">
          <div className="em-card">
            <div className="lbl">Just sourcing</div>
            <h3 className="nm">Sourcing.</h3>
            <p className="says">Ann fills the top of your funnel with the people you're missing. <em>You</em> evaluate, interview, decide.</p>
            <p className="who">When sourcing is your bottleneck. Works alongside your process — no rip-and-replace.</p>
            <div className="stages">
              <div className="step on">Find</div>
              <div className="step off">Eval</div>
              <div className="step off">Inter</div>
              <div className="step off">Dec</div>
            </div>
          </div>
          <div className="em-card featured">
            <div className="lbl">All of it · recommended</div>
            <h3 className="nm">The <em>whole thing</em>.</h3>
            <p className="says">Hand Ann the role. Get back a <em>decision-ready</em> shortlist — the case for each name on it.</p>
            <p className="who">When you want the connective tissue — the signal lost between disconnected tools, because nothing's disconnected.</p>
            <div className="stages">
              <div className="step on">Find</div>
              <div className="step on">Eval</div>
              <div className="step on">Inter</div>
              <div className="step on">Dec</div>
            </div>
          </div>
          <div className="em-card">
            <div className="lbl">Just interviews</div>
            <h3 className="nm">Interviews.</h3>
            <p className="says">Bring your pipeline. Ann runs the rounds <em>comparable</em>: same rubric, same calibration, every candidate.</p>
            <p className="who">When the pipeline's healthy but evaluation drifts between interviewers. Structured rounds, one report.</p>
            <div className="stages">
              <div className="step off">Find</div>
              <div className="step off">Eval</div>
              <div className="step on">Inter</div>
              <div className="step on">Dec</div>
            </div>
          </div>
        </div>
      </section>

      {/* BUILT FOR */}
      <section className="e-bf" id="built">
        <div className="head">
          <span className="eye">Built for</span>
          <div className="spacer-2" />
          <h2>
            Whoever owns the offer, Ann has your <em>back</em>.
          </h2>
        </div>

        <div className="grid">
          <div className="item">
            <div className="role">For Founders &amp; CEOs</div>
            <div className="h">Hire like you have a <em>10-person</em> talent team.</div>
            <p className="says">Hand Ann the role. Get back the <em>five people worth meeting</em> — and the case for each. Your week goes back to candidates, not the search.</p>
            <div className="meta">
              <strong>End-to-end</strong>
              1 – 5 critical hires per quarter
            </div>
            <Link to="/for/founders" className="item-link">For founders &amp; CTOs →</Link>
          </div>
          <div className="item">
            <div className="role">For Heads of TA</div>
            <div className="h">Quality at <em>volume</em>. And evidence your hiring managers actually trust.</div>
            <p className="says">12,000 résumés read the way your <em>sharpest sourcer</em> reads one. Your team meets the right people. <b>The forwarding moment stops being a fight.</b></p>
            <div className="meta">
              <strong>Search + Evaluate</strong>
              500 – 5,000 hires / year
            </div>
            <Link to="/for/heads-of-ta" className="item-link">For Heads of TA →</Link>
          </div>
          <div className="item">
            <div className="role">For CHROs &amp; people leaders</div>
            <div className="h">Hiring you can <em>stand behind</em>, the day someone asks how.</div>
            <p className="says">Every verdict comes with the evidence behind it. <em>Structured. Comparable. Auditable.</em> <b>The decision stays human</b> — the case is on the page.</p>
            <div className="meta">
              <strong>End-to-end · auditable</strong>
              Mature orgs · regulated industries
            </div>
            <Link to="/for/chros" className="item-link">For CHROs →</Link>
          </div>
        </div>

        <Link className="also" to="/for/staffing-firms">
          <div className="l">
            <div className="role">For staffing &amp; recruitment firms</div>
            <div className="h">Run an agency desk? Ann works <em>per client, per mandate</em> →</div>
          </div>
          <span className="go">For staffing firms →</span>
        </Link>
      </section>

      {/* PROOF */}
      <section className="e-proof">
        <span className="eye">In early use</span>
        <p className="quote">
          "It's the first hiring tool that <span className="cy">argues with me</span>. The shortlist arrives with its own case — and the case is usually <em className="wm">right</em>."
        </p>
        <p className="attrib">
          <span className="nm">Head of Talent</span> · Series B fintech · Bengaluru
        </p>
        <div className="t-marquee" aria-label="Client testimonials">
          <div className="t-track">
            {[...testimonials, ...testimonials].map((t, i) => (
              <figure className="t-card" key={i}>
                <blockquote className="t-text">{t.text}</blockquote>
                <figcaption className="t-author">
                  <img className="t-av" src={t.avatar} alt={t.name} />
                  <span className="t-meta">
                    <span className="t-name">{t.name}</span>
                    <span className="t-title">{t.title}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="e-faq" id="faq">
        <div className="head">
          <span className="eye">FAQ</span>
          <div className="spacer-2" />
          <h2>
            The questions <em>first asked</em>.
          </h2>
          <p className="lede">Short answers. The longer ones are best in a live conversation.</p>
        </div>
        <div className="items">
          <details className="faq-item">
            <summary><span className="num">01</span><span className="q">Can you run a 200-hire month — and a single-CFO search?</span><span className="ico">+</span></summary>
            <p className="a">Yes — same intelligence, different gear. Volume runs at scale through QuickSearch; single-role headhunting through TalentGPS, with deeper mapping and deliberate outreach. Many teams run both in one workspace.</p>
          </details>
          <details className="faq-item">
            <summary><span className="num">02</span><span className="q">We already have an ATS. Why add Ann?</span><span className="ico">+</span></summary>
            <p className="a">An ATS stores candidates and tracks stages. Ann <em>judges</em> them — and writes the judgement back into your ATS. Different job, different layer. Two-way sync with Greenhouse, Lever, Ashby, and Workday.</p>
          </details>
          <details className="faq-item">
            <summary><span className="num">03</span><span className="q">Will my hiring managers trust your verdict?</span><span className="ico">+</span></summary>
            <p className="a">They don't trust the verdict — they trust the <em>evidence</em>: cited work, comparable interviews, the gaps that don't fit. The decision stays human. Most managers read the confidence report first.</p>
          </details>
          <details className="faq-item">
            <summary><span className="num">04</span><span className="q">How do you handle bias?</span><span className="ico">+</span></summary>
            <p className="a">Ann scores against the role you approve. <em>Nothing else</em>. Every score is explainable; every evaluation is auditable. Our methodology is published and independently reviewed.</p>
          </details>
          <details className="faq-item">
            <summary><span className="num">05</span><span className="q">Where does our candidate data live?</span><span className="ico">+</span></summary>
            <p className="a">In your dedicated workspace, encrypted at rest. Ann does not train on your data — your pool is your pool. DPAs supported; EU and India residency on request.</p>
          </details>
          <details className="faq-item">
            <summary><span className="num">06</span><span className="q">How fast until we see something useful?</span><span className="ico">+</span></summary>
            <p className="a">Day one, a first read on the brief. Day two, the first shortlist with reasoning. By week's end, you've run a real role end-to-end with Ann.</p>
          </details>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="e-cta" id="cta">
        <div className="grid">
          <div>
            <div className="sig">
              <span className="g" />
              <span className="nm">The offer</span>
              <span className="pulse">— live</span>
            </div>
            <h2>
              You've seen what Ann sees. <b>Now find the one <em>you've</em> been missing.</b>
            </h2>
            <p className="lede-l">
              Fifteen minutes, on a real role off your desk. <em>Your data.</em> Ann
              surfaces the candidate your funnel never showed you, and tells you
              what it'd do next. Wrong, and you've spent fifteen minutes. Right,
              and you'll want the next conversation.
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                e.currentTarget.querySelector("button").textContent =
                  "Thanks — we'll be in touch.";
              }}
            >
              <input type="email" placeholder="you@yourcompany.com" required />
              <button type="submit" className="btn btn-primary btn-lg">Book the 15 minutes</button>
            </form>
            <p className="micro">No credit card · your data stays yours · DPA on request</p>
          </div>

          <div className="offer-card">
            <div className="ttl">What's needed from you</div>
            <div className="li">
              <span className="n">01</span>
              <p className="txt"><b>One role.</b> Bulk hire, specialised hire, or leadership search — your call.</p>
            </div>
            <div className="li">
              <span className="n">02</span>
              <p className="txt"><b>The JD.</b> Or just a paragraph from the hiring manager. <em>Either works.</em></p>
            </div>
            <div className="li">
              <span className="n">03</span>
              <p className="txt"><b>Fifteen minutes.</b> A live walkthrough of the first read, the search, and a candidate or two.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
